import { useEffect } from "react";
import { motion } from "framer-motion";
import Header from "@/components/landing-v2/Header";
import Footer from "@/components/landing-v2/Footer";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const ARTICLE_TITLE = "Jak zacząć nagrywać wideo do internetu? Kompletny poradnik dla początkujących";
const ARTICLE_DESCRIPTION = "Jak zacząć nagrywać wideo do internetu? Brzmi prosto, ale każdy kto choć raz usiadł przed kamerą wie, że to nie takie oczywiste. Poradnik krok po kroku.";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" as const },
  }),
};

const BlogArticle = () => {
  useEffect(() => {
    document.title = ARTICLE_TITLE;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", ARTICLE_DESCRIPTION);
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "description";
      newMeta.content = ARTICLE_DESCRIPTION;
      document.head.appendChild(newMeta);
    }
    return () => {
      document.title = "Viraluj – Przestań wymyślać. Zacznij robić viralowe wideo";
    };
  }, []);

  return (
    <div className="min-h-screen text-gray-900">
      <Header />

      <section className="relative pt-28 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9B896] via-[#FABFA3] to-[#FCCAB0]" />

        <article className="relative z-10 max-w-3xl mx-auto px-4">
          {/* Back link */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Wróć do bloga
            </Link>
          </motion.div>

          {/* Hero */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={1} className="mb-10">
            <span className="inline-block text-xs font-medium px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 mb-4">
              Poradnik
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 leading-tight">
              Jak zacząć nagrywać wideo do internetu? Kompletny poradnik dla początkujących
            </h1>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                18 lut 2026
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                12 min czytania
              </span>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 p-6 sm:p-10 prose prose-gray max-w-none"
          >
            <p className="lead text-lg text-gray-700">
              Jak zacząć nagrywać wideo do internetu? Brzmi prosto, ale każdy kto choć raz usiadł przed kamerą wie, że to nie takie oczywiste. Denerwuje, głos brzmi dziwnie, kadr jest krzywy, dźwięk szumi — znacie to?
            </p>

            <p>
              Dobra wiadomość jest taka, że każdy kto dziś publikuje regularne wideo w internecie — kiedyś zaczynał dokładnie tak samo jak Ty. Ze smartfonem w ręku, trzęsącymi się kolanami i kompletnym brakiem pomysłu co powiedzieć. Dlatego ten poradnik powstał — żebyś przeskoczył kilka niepotrzebnych błędów i zaczął nagrywać wideo mądrzej.
            </p>

            <p>
              Nie będę Cię karmił teorią. Dostaniesz konkretne wskazówki: jaki sprzęt wybrać na start, jak ustawić kadr, co zrobić z dźwiękiem, jak wyglądać przed kamerą i jak w końcu opublikować swój pierwszy film.
            </p>

            <h2>Czy musisz mieć drogi sprzęt żeby zacząć nagrywać wideo?</h2>

            <p>
              Zacznę od rzeczy, która zatrzymuje 90% ludzi w miejscu zanim jeszcze zaczną. Mitu drogiego sprzętu. Krótka odpowiedź: nie. Długa odpowiedź: też nie, ale zrozum dlaczego.
            </p>

            <p>
              Smartfon, który masz teraz w kieszeni nagrywa w rozdzielczości 4K. To lepszy sprzęt niż ten, którym kręcono profesjonalne reklamy 10 lat temu. Problem nie leży w kamerze — leży w tym co przed nią. Czyli w Tobie, w świetle, w dźwięku i w ustawieniu.
            </p>

            <p>Zanim więc zaczniesz szukać kamer za kilka tysięcy złotych, zrób tak:</p>

            <ol>
              <li>Nagraj film telefonem.</li>
              <li>Obejrzyj go.</li>
              <li>Znajdź 3 największe problemy.</li>
              <li>Kup sprzęt który rozwiązuje TYLKO te problemy.</li>
            </ol>

            <p>
              W 95% przypadków pierwszym problemem okaże się dźwięk. Nie obraz — dźwięk. I tu dochodzimy do pierwszego konkretnego zakupu, który faktycznie robi różnicę.
            </p>

            <h2>Jaki sprzęt do nagrywania wideo wybrać na start?</h2>

            <p>Omówię to w kolejności priorytetów — od najważniejszego do najmniej ważnego. Bo większość ludzi kupuje dokładnie odwrotnie.</p>

            <h3>1. Mikrofon — priorytet numer jeden</h3>

            <p>
              Widzowie wybaczą Ci słaby obraz. Nie wybaczą złego dźwięku. To zasada, która nie ma wyjątków. Jeśli Twój głos szumi, pogłos jest ogromny albo słychać każde kliknięcie klawiatury w tle — ludzie wyłączą film po 15 sekundach.
            </p>

            <p>Na start wystarczą dwie opcje:</p>

            <ul>
              <li>
                <strong>Mikrofon krawatowy (lavalier)</strong> — małe urządzenie które przypinasz do koszuli. Kosztuje od 50 do 200 zł i robi kolosalną różnicę względem mikrofonu wbudowanego w telefon. Świetny wybór do filmów gdzie dużo się ruszasz.
              </li>
              <li>
                <strong>Mikrofon kierunkowy (shotgun)</strong> do kamery lub na stół — lepsza opcja jeśli nagrywasz w jednym miejscu, np. za biurkiem. Zbiera dźwięk przede wszystkim z przodu i odcina szumy z boków.
              </li>
            </ul>

            <h3>2. Oświetlenie — darmowe lub tanie</h3>

            <p>
              Dobre oświetlenie może sprawić, że film nagrany telefonem wygląda jak materiał z profesjonalnej produkcji. Złe oświetlenie sprawi, że nawet kamera za 5000 zł da przeciętny efekt.
            </p>

            <p>
              Zacznij od darmowego rozwiązania: ustaw się twarzą do okna. Światło dzienne z okna to najlepsze miękkie oświetlenie jakie możesz dostać za 0 złotych. Ważne żeby okno było przed Tobą — nie za Tobą, bo inaczej będziesz ciemną sylwetką na jasnym tle.
            </p>

            <p>
              Jeśli nagrywasz wieczorami albo masz małe okno — kup ring light (okrągłe światło LED). Ceny zaczynają się od 80-100 zł. To jeden z najlepszych zakupów dla początkującego twórcy wideo.
            </p>

            <h3>3. Kamera — smartfon w zupełności wystarczy</h3>

            <p>
              Powtarzam to jeszcze raz, bo wiem że wielu z Was i tak będzie szukać kamery: zacznij od smartfona. Jeśli masz telefon z ostatnich 3-4 lat — masz kamerę wystarczającą do startu. Naprawdę.
            </p>

            <p>
              Gdy poczujesz, że telefon Cię ogranicza i zaczniesz rozumieć czego konkretnie Ci brakuje — wtedy rozmawiamy o kamerze. Popularne wybory wśród twórców na YouTube i TikToku to Sony ZV-E10, Canon M50 Mark II lub GoPro do nagrywania w ruchu.
            </p>

            <h3>4. Statyw albo uchwyt — konieczna stabilizacja</h3>

            <p>
              Trzęsący się obraz to jeden z najpewniejszych sposobów na zniechęcenie widza. Trzymanie telefonu w ręku podczas nagrywania to zły pomysł, chyba że świadomie wybierasz estetykę "z ręki". Na start kup prosty statyw z uchwytem na telefon — to wydatek rzędu 50-80 zł.
            </p>

            <h2>Jak ustawić kadr podczas nagrywania wideo?</h2>

            <p>
              Złe ustawienie kadru sprawia, że nawet najlepiej powiedziana treść wygląda amatorsko. Kilka zasad:
            </p>

            <ul>
              <li><strong>Oczy na poziomie 1/3 od góry kadru</strong> — zasada trójpodziału. Nigdy nie umieszczaj oczu na środku ekranu.</li>
              <li><strong>Kamera na poziomie oczu lub lekko powyżej</strong> — nigdy poniżej. Lekko z góry działa korzystnie dla każdej twarzy.</li>
              <li><strong>Zostaw wolne miejsce nad głową</strong> — trochę, ale nie za dużo. Tzw. "headroom".</li>
              <li><strong>Zadbaj o tło</strong> — uporządkowane, ciekawe wizualnie, ale nie rozpraszające. Regał z książkami, neutralna ściana, roślina.</li>
            </ul>

            <p>
              Jeśli nagrywasz pionowo (TikTok, Reels, Shorts) — zasady są te same, tylko format zmienia się z 16:9 na 9:16.
            </p>

            <h2>Jak wyglądać i mówić przed kamerą?</h2>

            <p>
              To jest najtrudniejsza część. Sprzęt można kupić, kadru można się nauczyć — ale bycie naturalnym przed kamerą to umiejętność, którą trzeba wypracować.
            </p>

            <ul>
              <li>
                <strong>Mów do jednej osoby, nie do tłumu.</strong> Wyobraź sobie jedną konkretną osobę — znajomego, klienta, brata — i mów tylko do niej. To natychmiastowo zmienia energię. Przestajesz "prezentować", zaczynasz rozmawiać.
              </li>
              <li>
                <strong>Patrz w obiektyw, nie w ekran.</strong> Największy błąd początkujących: patrzą w swój obraz na ekranie zamiast w obiektyw kamery. Obiektyw to oczy widza — utrzymuj z nim kontakt.
              </li>
            </ul>

            <h3>Przygotuj szkielet, nie skrypt</h3>

            <p>
              Uczenie się tekstu na pamięć i recytowanie go przed kamerą brzmi sztucznie i jest koszmarem w montażu. Zamiast tego: napisz 3-5 punktów które chcesz omówić i mów naturalnie. Możesz powtórzyć fragment 10 razy — w montażu zostanie ta jedna dobra wersja.
            </p>

            <h3>Zadbaj o wygląd — to wideo, widać wszystko</h3>

            <p>
              Włosy, zarost, ubranie — na wideo widać każdy szczegół. Nie musisz przychodzić w garniturze, ale zadbane włosy i schludna koszula to absolutne minimum.
            </p>

            <h2>Jak nagrać pierwsze wideo krok po kroku?</h2>

            <p>Masz sprzęt, wiesz jak ustawić kadr i rozumiesz podstawy bycia przed kamerą. Czas działać:</p>

            <ol>
              <li>Przygotuj temat i 3-5 punktów do omówienia — zapisz je na kartce lub na ekranie obok kamery.</li>
              <li>Ustaw oświetlenie — okno przed sobą lub ring light.</li>
              <li>Ustaw kamerę / telefon na statywie, sprawdź kadr.</li>
              <li>Podłącz mikrofon, nagraj 10 sekund testowo i odsłuchaj — sprawdź czy nie ma szumów.</li>
              <li>Nagraj intro — przedstaw temat w jednym zdaniu. To Twój hook.</li>
              <li>Przejdź przez kolejne punkty — nie przejmuj się błędami, zatrzymuj się i zaczynaj zdanie od nowa.</li>
              <li>Nagraj zakończenie z wyraźnym wezwaniem do działania (subskrypcja, komentarz, link).</li>
            </ol>

            <h2>Czym montować wideo? Programy do edycji dla początkujących</h2>

            <ul>
              <li><strong>CapCut</strong> — darmowy, dostępny na telefon i komputer. Idealny na start, szczególnie do krótkich form pionowych (TikTok, Reels). Prosty, intuicyjny i ma wbudowane napisy automatyczne.</li>
              <li><strong>DaVinci Resolve</strong> — darmowy program na komputer o możliwościach profesjonalnych. Wymaga chwili nauki, ale jest bezpłatny w podstawowej wersji.</li>
              <li><strong>Adobe Premiere Pro</strong> — płatny (subskrypcja), ale najbardziej rozbudowany. Polecany gdy już wiesz że wideo to Twoja droga.</li>
            </ul>

            <p>Na start nie komplikuj. CapCut wystarczy żeby opublikować pierwsze 50 filmów i zbudować nawyk nagrywania.</p>

            <h2>Na jaką platformę wrzucać wideo?</h2>

            <ul>
              <li><strong>YouTube</strong> — najlepsza platforma do dłuższego contentu (5-20 minut). Buduje autorytet, dobrze pozycjonuje się w Google, generuje ruch przez lata.</li>
              <li><strong>TikTok</strong> — king krótkich pionowych form (15 sekund – 3 minuty). Ogromny zasięg organiczny, idealna platforma do testowania hooków i tematów.</li>
              <li><strong>Instagram Reels</strong> — podobny format do TikToka, ale inna społeczność. Reels mają teraz największy zasięg organiczny na Instagramie.</li>
              <li><strong>YouTube Shorts</strong> — krótkie formy na YouTube. Dobry sposób żeby zdobyć subskrybentów i nakierować ich na długie filmy.</li>
            </ul>

            <p>Rada: zacznij od jednej platformy i opanuj ją zanim ruszysz na kolejną. Rozpraszanie się na 4 platformy naraz to przepis na wypalenie.</p>

            <h2>Najczęstsze błędy przy nagrywaniu pierwszych wideo</h2>

            <ul>
              <li><strong>Czekanie na "idealny sprzęt"</strong> — nie ma idealnego momentu. Zacznij teraz, z tym co masz.</li>
              <li><strong>Perfekcjonizm w montażu</strong> — lepszy film opublikowany niż idealny nigdy nieukończony.</li>
              <li><strong>Zły dźwięk zignorowany</strong> — widzowie wybaczą słaby obraz, nie wybaczą złego dźwięku.</li>
              <li><strong>Brak planu treści</strong> — nagrywanie "na temat który przyszedł do głowy" bez strategii to droga donikąd.</li>
              <li><strong>Rezygnacja po kilku filmach</strong> — algorytmy nagradzają regularność. Pierwsze 30 filmów to szkoła nagrywania, nie mierzenie sukcesu.</li>
              <li><strong>Słabe intro / brak hooka</strong> — jeśli pierwsze 3 sekundy nie zatrzymują widza, reszty nie zobaczy nikt.</li>
            </ul>

            <h2>Podsumowanie</h2>

            <p>
              Smartfon + dobre oświetlenie + prosty mikrofon to zestaw za mniej niż 200 zł, który pozwoli Ci tworzyć wideo na poziomie wyższym niż 80% konkurencji. Resztę — czyli bycie naturalnym przed kamerą, rytm montażu i strategię treści — wypracujesz z każdym kolejnym filmem.
            </p>

            <p>
              Jedyną drogą do przodu jest zacząć — a nie czekać.
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={3}
            className="mt-10 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 p-8 text-center"
          >
            <h2 className="font-semibold text-xl mb-2">Chcesz tworzyć lepsze scenariusze do swoich filmów?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Viraluj.pl pomoże Ci generować angażujące scenariusze na podstawie treści, które już działają.
            </p>
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium text-sm px-6 py-3 rounded-full hover:opacity-90 transition-opacity"
            >
              Wypróbuj Viraluj.pl
            </Link>
          </motion.div>
        </article>
      </section>

      <Footer />
    </div>
  );
};

export default BlogArticle;
